import { useMemo, useState } from "react";
import classNames from "classnames";
import type { FocusEventHandler, ReactNode } from "react";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemValue } from "@design-system/Picker/Picker.types";
import { styleWithVars } from "@lib/cssVars";
import styles from "./FilterMultiselect.module.scss";

export type FilterMultiselectValue = PickerItemValue;

/** One checkable row. `label` may be any node; `searchableLabel` is what the search matches when it is not a string or number. */
export interface FilterMultiselectOption<
    T extends FilterMultiselectValue = FilterMultiselectValue,
> {
    value: T;
    label: ReactNode;
    searchableLabel?: string;
    /** Rows sharing a `section` render under that title, sections in first-seen order. */
    section?: string;
    /** Shown when hovering the row label. */
    tooltip?: ReactNode;
}

interface FilterMultiselectSharedProps<T extends FilterMultiselectValue> {
    options: ReadonlyArray<FilterMultiselectOption<T>>;
    value: T[];
    onChange: (value: T[]) => void;
    /** Search bar above the rows (default `true`). */
    searchable?: boolean;
    /** Replaces the default match: string or number label, else `searchableLabel`. */
    filterFn?: (search: string, option: FilterMultiselectOption<T>) => boolean;
    /** Adds a "Reset" row under the list while something is selected. */
    onReset?: () => void;
    name?: string;
    onBlur?: FocusEventHandler<HTMLElement>;
}

interface FilterMultiselectInlineProps {
    /** Renders the list in place (filter panels) instead of behind a button. */
    inDropdown: false;
    /** Cap of the scrolling rows in px (default 410). */
    maxListHeight?: number;
    /** `"panel"` renders every row and leaves scrolling to the surrounding panel (default `"list"`). */
    scrollContainer?: "list" | "panel";
    buttonText?: never;
}

interface FilterMultiselectDropdownProps<T extends FilterMultiselectValue> {
    inDropdown?: true;
    /** Button label; defaults to "Select" / "All" / "a, b" / "a, b, and N more". */
    buttonText?:
        | ReactNode
        | ((
              value: T[],
              options: ReadonlyArray<FilterMultiselectOption<T>>,
          ) => ReactNode);
    maxListHeight?: never;
    scrollContainer?: never;
}

export type FilterMultiselectProps<T extends FilterMultiselectValue> =
    FilterMultiselectSharedProps<T> &
        (FilterMultiselectInlineProps | FilterMultiselectDropdownProps<T>);

const MAX_LISTED_LABELS = 2;

const matchesOption = <T extends FilterMultiselectValue>(
    search: string,
    option: FilterMultiselectOption<T>,
) => {
    const query = search.toLowerCase();
    if (!option.label) {
        return false;
    }
    if (typeof option.label === "string" || typeof option.label === "number") {
        return String(option.label).toLowerCase().includes(query);
    }
    return option.searchableLabel?.toLowerCase().includes(query) ?? false;
};

const getOptionText = <T extends FilterMultiselectValue>(
    option: FilterMultiselectOption<T>,
): string =>
    typeof option.label === "string" || typeof option.label === "number"
        ? String(option.label)
        : (option.searchableLabel ?? "");

const renderSummary = <T extends FilterMultiselectValue>(
    value: T[],
    options: ReadonlyArray<FilterMultiselectOption<T>>,
): ReactNode => {
    if (value.length === 0) {
        return "Select";
    }
    if (value.length === options.length) {
        return "All";
    }
    const labelsByValue = new Map(
        options.map((option) => [option.value, getOptionText(option)]),
    );
    const listedText = value
        .slice(0, MAX_LISTED_LABELS)
        .map((item) => labelsByValue.get(item))
        .join(", ");
    if (value.length <= MAX_LISTED_LABELS) {
        return <BreakableText className={styles.summary} text={listedText} />;
    }
    return (
        <span className={styles.summaryRow}>
            <BreakableText className={styles.summary} text={listedText} />
            <span>, and {value.length - MAX_LISTED_LABELS} more</span>
        </span>
    );
};

const groupBySection = <T extends FilterMultiselectValue>(
    options: ReadonlyArray<FilterMultiselectOption<T>>,
) => {
    const groups = new Map<string, Array<FilterMultiselectOption<T>>>();
    options.forEach((option) => {
        const section = option.section ?? "";
        groups.set(section, [...(groups.get(section) ?? []), option]);
    });
    return Array.from(groups, ([title, sectionOptions]) => ({
        title,
        options: sectionOptions,
    }));
};

/**
 * The filter-panel multi-select: checkable rows (optionally sectioned, with
 * row tooltips and node labels), a search bar and an optional Reset row, on
 * `Picker.Select multiple`. `inDropdown={false}` renders the list in place;
 * otherwise a full-width primary button opens it in a popup.
 */
export const FilterMultiselect = <T extends FilterMultiselectValue>(
    props: FilterMultiselectProps<T>,
) => {
    const {
        options,
        value,
        onChange,
        searchable: isSearchable = true,
        filterFn = matchesOption,
        onReset,
        name,
        onBlur,
    } = props;
    const [search, setSearch] = useState("");

    const groups = useMemo(
        () =>
            groupBySection(
                options.filter((option) => filterFn(search, option)),
            ),
        [filterFn, options, search],
    );

    const body = (
        <>
            {isSearchable && <Picker.Search autoFocus />}
            <Picker.List className={styles.list}>
                {groups.map((group, index) => (
                    <FilterMultiselectGroup
                        key={group.title}
                        title={group.title}
                        options={group.options}
                        hasSeparator={index > 0}
                    />
                ))}
                {groups.length === 0 && (
                    <Picker.Empty>No matching results</Picker.Empty>
                )}
            </Picker.List>
            {onReset && value.length > 0 && (
                <Picker.Footer>
                    <Picker.Item onSelect={onReset} closeOnSelect={false}>
                        Reset
                    </Picker.Item>
                </Picker.Footer>
            )}
        </>
    );

    const rootProps = {
        value,
        onValueChange: onChange,
        inputValue: search,
        onInputValueChange: setSearch,
        name,
        onBlur,
    };

    if (props.inDropdown === false) {
        return (
            <div
                className={classNames(styles.root, styles.inlineRoot)}
                data-scroll-container={props.scrollContainer ?? "list"}
                style={
                    props.maxListHeight === undefined
                        ? undefined
                        : styleWithVars({
                              "--filter-list-max-height": `${props.maxListHeight}px`,
                          })
                }
            >
                <Picker.Select {...rootProps} multiple inline>
                    {body}
                </Picker.Select>
            </div>
        );
    }

    const { buttonText = renderSummary } = props;
    return (
        <div className={styles.root}>
            <Picker.Select
                {...rootProps}
                multiple
                searchable={isSearchable}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setSearch("");
                    }
                }}
            >
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            block
                            className={styles.trigger}
                        />
                    }
                    value={
                        typeof buttonText === "function"
                            ? buttonText(value, options)
                            : buttonText
                    }
                />
                <Picker.Popup>{body}</Picker.Popup>
            </Picker.Select>
        </div>
    );
};

const FilterMultiselectGroup = <T extends FilterMultiselectValue>({
    title,
    options,
    hasSeparator,
}: {
    title: string;
    options: ReadonlyArray<FilterMultiselectOption<T>>;
    hasSeparator: boolean;
}) => {
    const rows = options.map((option) => (
        <Picker.Item
            key={option.value}
            value={option.value}
            tooltip={option.tooltip}
        >
            {option.label}
        </Picker.Item>
    ));
    return (
        <>
            {hasSeparator && <Picker.Separator />}
            {title === "" ? (
                rows
            ) : (
                <Picker.Section>
                    <Picker.SectionTitle>{title}</Picker.SectionTitle>
                    {rows}
                </Picker.Section>
            )}
        </>
    );
};
